// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, InputAdornment, LinearProgress,
  MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs,
  TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HubIcon from '@mui/icons-material/Hub';
import LockIcon from '@mui/icons-material/Lock';
import LinkIcon from '@mui/icons-material/Link';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import { isMaster } from './auth';
import { getConfig } from './config';
import { exportCsv } from './exports';
import store from './store';

// ---------------------------------------------------------------------------
// Catalog Intelligence — the cross-client product view.
//
// The scenario this exists for: a consultant is on a call, the prospect says
// "we run Omnicell", and within seconds we should be able to say which existing
// clients run it, at which versions, hosted how, what disposition each chose,
// and what went wrong last time. LeanIX cannot do this at all — it is
// single-tenant by construction. Optimum holds every engagement in one place,
// so the comparison is ours to make.
//
// ***  SECURITY BOUNDARY — MASTER ONLY. READ BEFORE CHANGING ANYTHING.  ***
//
// This screen renders NAMED clients and full per-client, row-level detail:
// application names, versions, owners, hosting, dispositions and free-text
// lessons learned. It is Optimum-internal and must NEVER render for a client
// user. If it did, one client would see another client's estate by name.
//
// That is a DIFFERENT contract from Benchmarks.js. Benchmarks is anonymised and
// k-anonymous — no client is named, nothing below a cohort minimum is published
// — precisely so that it could in principle be walked through with a client.
// Nothing on this screen is anonymised, so no such possibility exists here.
//
// The gate is the same `isMaster()` check Benchmarks uses, and it is a hard
// refusal: a non-master user gets a plain explanation and no data is even
// fetched. Do not soften it, do not add a "read-only client view", do not add a
// share/export path that reaches a client. If a client needs this material, a
// consultant reads it out loud — that is a human decision about one specific
// client, not a permission grant.
//
// ---------------------------------------------------------------------------
// WHY LINKING IS A HUMAN DECISION (the honesty rule of this file):
//
// Measured on the current corpus: 584 client Applications across 4 client
// tenants, ZERO of them linked to a catalog entry, and only 5 catalog rows
// exist. Storage was never the missing piece — the linking workflow was.
//
// Clients also name the same product completely differently. NGHS writes
// "Vendor + Product" ("BD Alaris", "3M 360 Encompass"); DFCI writes
// "Product - dfci" ("Alaris Systems Manager - DFCI", "Zoom - DFCI").
//
// Fuzzy matching across the estate yields roughly 18 candidate pairs at full
// containment after normalisation — and about a THIRD of those are wrong.
// "Crestron Fusion" is not "Fusion-Rx". "vRealize Log Insight" is not
// "InSight". "Workday Studio" is not "ER Studio". They are different products
// that happen to share a distinctive-looking word.
//
// Therefore this tool PROPOSES with a score and the evidence behind it, and a
// human CONFIRMS. It never auto-links. A wrong link silently tells a consultant
// that a client runs software they do not run, on a call, out loud. Bulk
// confirm is offered ONLY for exact normalised-name matches, never for fuzzy
// ones, and no fuzzy candidate is ever displayed as though it were confirmed.
// ---------------------------------------------------------------------------

// Optimum palette.
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const DIFF_BG = '#FFF4E0';
const DIFF_LINE = '#E39A00';

const MASTER_TENANT = 'optimum';

// Render caps. Counts printed to the user are ALWAYS computed over the full
// set — only the DOM is capped.
const ROW_CAP = 200;
const GROUP_CAP = 25;

// Candidate generation / scoring knobs.
const MIN_SCORE = 0.30;      // below this a candidate is noise, not a proposal
const MAX_CANDIDATES = 5;    // per unlinked application
const DF_CAP = 60;           // a token in >60 records is too common to seed candidates
const FUZZY_CEILING = 0.95;  // only an exact normalised match may ever read 100%
const SINGLE_TOKEN_CEILING = 0.55; // "InSight" contained in "vRealize Log Insight"

// ---------------------------------------------------------------------------
// Normalisation
//
// Two token classes get stripped before comparison:
//   1. CLIENT tokens — the client's own initialism, which several clients staple
//      onto every app name ("Zoom - DFCI"). It carries no product information
//      and, left in, it makes every one of a client's apps look similar to every
//      other one of that client's apps.
//   2. CORPORATE tokens — vendor-suffix and sector filler ("Systems", "Inc",
//      "Health"). These are the words most likely to collide across unrelated
//      products.
// The first list is the tenant initialisms we actually see in the corpus; the
// second is the spec'd suffix list plus obvious singular/plural siblings.
// ---------------------------------------------------------------------------
const CLIENT_TOKENS = new Set(['dfci', 'nghs', 'musc', 'muha', 'humi', 'enta']);
const CORP_TOKENS = new Set([
  'inc', 'llc', 'llp', 'ltd', 'corp', 'co',
  'systems', 'system', 'solutions', 'solution', 'technologies', 'technology',
  'medical', 'health', 'healthcare',
]);

// Tokenise a name down to its product-bearing words.
export function normTokens(name) {
  const raw = String(name == null ? '' : name)
    .toLowerCase()
    .replace(/[‘’“”]/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  if (!raw) return [];
  const all = raw.split(' ').filter((t) => t.length > 1 || /[0-9]/.test(t));
  const kept = all.filter((t) => !CLIENT_TOKENS.has(t) && !CORP_TOKENS.has(t));
  // If stripping removed everything ("Health Systems Inc"), keep the raw tokens
  // rather than produce an empty key that would match every other empty key.
  return kept.length ? kept : all;
}

// Sorted key so word order cannot make two identical names look different.
const keyOf = (tokens) => tokens.slice().sort().join(' ');

// Human-facing canonical name: drop the client initialism, keep original casing.
export function canonicalName(raw) {
  const words = String(raw == null ? '' : raw).split(/\s+/).filter(Boolean);
  const kept = words.filter((w) => {
    const bare = w.replace(/[^A-Za-z0-9]/g, '').toLowerCase();
    return bare === '' ? true : !CLIENT_TOKENS.has(bare);
  });
  const out = kept
    .join(' ')
    .replace(/\s*[-–—|/]+\s*$/, '')
    .replace(/^\s*[-–—|/]+\s*/, '')
    .trim();
  return out || String(raw == null ? '' : raw).trim();
}

// ---------------------------------------------------------------------------
// Field accessors — the columns a consultant actually reads out on a call.
// ---------------------------------------------------------------------------
const f = (a, k) => {
  const v = a && a.fields ? a.fields[k] : undefined;
  return v === undefined || v === null || String(v).trim() === '' ? '' : String(v).trim();
};

const COMPARE_ROWS = [
  { key: 'name', label: 'Their app name', of: (a) => a.name || '', diff: false },
  { key: 'version', label: 'Version', of: (a) => f(a, 'version'), diff: true },
  { key: 'hostingType', label: 'Hosting type', of: (a) => f(a, 'hostingType'), diff: true },
  { key: 'locationType', label: 'Location', of: (a) => f(a, 'locationType'), diff: true },
  { key: 'timeClassification', label: 'TIME', of: (a) => f(a, 'timeClassification'), diff: true },
  {
    key: 'sixR',
    label: '6R disposition',
    of: (a) => f(a, 'sixRClassification') || f(a, 'cmDisposition'),
    diff: true,
  },
  { key: 'businessCriticality', label: 'Criticality', of: (a) => f(a, 'businessCriticality'), diff: true },
  { key: 'drTier', label: 'DR tier', of: (a) => f(a, 'drTier'), diff: true },
  { key: 'vendorName', label: 'Vendor', of: (a) => f(a, 'vendorName'), diff: true },
  { key: 'applicationOwner', label: 'Business owner', of: (a) => f(a, 'applicationOwner'), diff: false },
  { key: 'technicalOwner', label: 'Technical owner', of: (a) => f(a, 'technicalOwner'), diff: false },
  { key: 'lifecycleState', label: 'Lifecycle', of: (a) => f(a, 'lifecycleState'), diff: true },
  { key: 'ssoAvailability', label: 'SSO', of: (a) => f(a, 'ssoAvailability'), diff: true },
  { key: 'rto', label: 'RTO', of: (a) => f(a, 'rto'), diff: true },
];

// Narrative fields. Never diff-highlighted — prose differs by definition; the
// value is in reading it, not in noticing it is not identical.
const NOTE_ROWS = [
  { key: 'lessonsLearned', label: 'Lessons learned', of: (a) => f(a, 'lessonsLearned') },
  { key: 'mustWhitelist', label: 'Must whitelist', of: (a) => f(a, 'mustWhitelist') },
  { key: 'awsGotchas', label: 'AWS gotchas', of: (a) => f(a, 'awsGotchas') },
  { key: 'azureGotchas', label: 'Azure gotchas', of: (a) => f(a, 'azureGotchas') },
  { key: 'hostingDescription', label: 'Hosting detail', of: (a) => f(a, 'hostingDescription') },
];

// ---------------------------------------------------------------------------
// Scoring
//
// CONTAINMENT, not Jaccard: "BD Alaris" and "Alaris Systems Manager" share only
// one word out of four, so Jaccard buries them — yet they are plainly the same
// infusion platform. Containment asks "how much of the SMALLER name is present
// in the larger one", which is the right question when one client writes the
// vendor and the other writes the product line.
//
// Containment alone is far too generous, so it is weighted by how distinctive
// each token is (inverse document frequency across the whole estate). Sharing
// "alaris" is evidence. Sharing "manager" is not.
//
// Two hard ceilings keep the score honest:
//   - a match where the shorter name is a SINGLE token cannot exceed 0.55,
//     because "InSight" is contained in "vRealize Log Insight" and they are
//     unrelated products;
//   - nothing but an identical normalised name may ever read 100%.
// ---------------------------------------------------------------------------
function buildIdf(records) {
  const df = new Map();
  records.forEach((r) => {
    new Set(r.tokens).forEach((t) => df.set(t, (df.get(t) || 0) + 1));
  });
  const n = records.length || 1;
  const idf = new Map();
  df.forEach((count, t) => idf.set(t, Math.log((n + 1) / (count + 1)) + 1));
  return { idf, df };
}

const weightOf = (tokens, idf) => tokens.reduce((sum, t) => sum + (idf.get(t) || 1), 0);

function scorePair(a, b, idf) {
  if (!a.tokens.length || !b.tokens.length) return null;
  const bSet = new Set(b.tokens);
  const matched = [...new Set(a.tokens)].filter((t) => bSet.has(t));
  if (!matched.length) return null;

  const exact = a.key === b.key;
  const wa = weightOf([...new Set(a.tokens)], idf);
  const wb = weightOf([...new Set(b.tokens)], idf);
  const wm = weightOf(matched, idf);
  const containment = wm / Math.max(1e-6, Math.min(wa, wb));

  const minTokens = Math.min(new Set(a.tokens).size, new Set(b.tokens).size);
  const flags = [];
  let score;
  if (exact) {
    score = 1;
  } else {
    score = Math.min(containment, FUZZY_CEILING);
    if (minTokens === 1) {
      score = Math.min(score, SINGLE_TOKEN_CEILING);
      flags.push('single-token overlap');
    }
    if (containment >= 0.999) flags.push('one name fully contained in the other');
  }

  const aSet = new Set(a.tokens);
  return {
    score,
    exact,
    flags,
    matched: matched.sort((x, y) => (idf.get(y) || 0) - (idf.get(x) || 0)),
    unmatchedLeft: [...aSet].filter((t) => !bSet.has(t)),
    unmatchedRight: [...bSet].filter((t) => !aSet.has(t)),
  };
}

function bandOf(m) {
  if (m.exact) return { label: 'Exact', color: '#2E7D32', bg: '#E8F5E9' };
  if (m.score >= 0.75) return { label: 'Strong', color: '#0D5B9A', bg: '#E7F1FA' };
  if (m.score >= 0.5) return { label: 'Partial', color: '#B26A00', bg: '#FFF4E0' };
  return { label: 'Weak', color: '#C62828', bg: '#FDECEA' };
}

const pct = (x) => `${Math.round(x * 100)}%`;
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

// ---------------------------------------------------------------------------
// Model — built once from the loaded corpus, reused by every tab.
// ---------------------------------------------------------------------------
function buildModel(apps, catalog, tenants) {
  const tenantsById = new Map((tenants || []).map((t) => [t.id, t]));

  // A catalog entry is either a seeded CATALOG row, or an Application fact sheet
  // living in the master workspace that points its own catalogId at itself. The
  // second form is what "create new catalog entry from this app" produces — it
  // uses only the existing store API and is a real, persistent record.
  const catalogEntries = [
    ...(catalog || []).map((c) => ({
      id: c.id,
      name: c.name,
      vendor: c.vendor || '',
      category: c.category || '',
      description: c.description || '',
      source: 'seed',
    })),
    ...(apps || [])
      .filter((a) => a.tenantId === MASTER_TENANT && a.type === 'Application' && f(a, 'catalogId') === a.id)
      .map((a) => ({
        id: a.id,
        name: a.name,
        vendor: f(a, 'vendorName'),
        category: '',
        description: f(a, 'description'),
        source: 'master',
      })),
  ].map((c) => {
    const tokens = normTokens(c.name);
    return { ...c, tokens, key: keyOf(tokens) };
  });
  const catalogById = new Map(catalogEntries.map((c) => [c.id, c]));

  // Client applications: every non-master tenant. Sample/demo tenants are kept
  // (they are real rows in the workspace) but their kind is shown, so nobody
  // quotes a demo estate to a prospect by accident.
  const clientApps = (apps || [])
    .filter((a) => a && a.type === 'Application' && a.tenantId && a.tenantId !== MASTER_TENANT)
    .map((a) => {
      const tokens = normTokens(a.name);
      const t = tenantsById.get(a.tenantId);
      const catalogId = f(a, 'catalogId');
      return {
        ...a,
        tokens,
        key: keyOf(tokens),
        clientName: (t && t.name) || a.tenantId,
        clientKind: (t && t.kind) || 'client',
        catalogId: catalogById.has(catalogId) ? catalogId : '',
        // A catalogId that resolves to nothing is recorded but not trusted.
        danglingCatalogId: catalogId && !catalogById.has(catalogId) ? catalogId : '',
      };
    });

  const { idf, df } = buildIdf([...clientApps, ...catalogEntries]);

  // Inverted index over client apps, for candidate generation.
  const tokenIndex = new Map();
  clientApps.forEach((a, i) => {
    new Set(a.tokens).forEach((t) => {
      if (!tokenIndex.has(t)) tokenIndex.set(t, []);
      tokenIndex.get(t).push(i);
    });
  });
  const catalogIndex = new Map();
  catalogEntries.forEach((c, i) => {
    new Set(c.tokens).forEach((t) => {
      if (!catalogIndex.has(t)) catalogIndex.set(t, []);
      catalogIndex.get(t).push(i);
    });
  });

  // Products: a catalog entry when linked, otherwise the normalised name.
  const products = new Map();
  clientApps.forEach((a) => {
    const pid = a.catalogId ? `cat:${a.catalogId}` : `norm:${a.key || a.name.toLowerCase()}`;
    if (!products.has(pid)) {
      const entry = a.catalogId ? catalogById.get(a.catalogId) : null;
      products.set(pid, {
        id: pid,
        linked: !!a.catalogId,
        catalogId: a.catalogId || '',
        label: entry ? entry.name : '',
        vendor: entry ? entry.vendor : '',
        key: a.key,
        members: [],
      });
    }
    products.get(pid).members.push(a);
  });

  const productList = [...products.values()].map((p) => {
    const clients = [...new Set(p.members.map((m) => m.tenantId))];
    let label = p.label;
    if (!label) {
      // Representative name for an unlinked group: the most common raw name,
      // ties broken by the shortest (least decorated) one.
      const counts = new Map();
      p.members.forEach((m) => counts.set(m.name, (counts.get(m.name) || 0) + 1));
      label = [...counts.entries()].sort(
        (x, y) => y[1] - x[1] || x[0].length - y[0].length || x[0].localeCompare(y[0])
      )[0][0];
      label = canonicalName(label);
    }
    const vendor = p.vendor || (p.members.map((m) => f(m, 'vendorName')).find(Boolean) || '');
    const haystack = [
      label, vendor, p.key,
      ...p.members.map((m) => m.name),
      ...p.members.map((m) => f(m, 'vendorName')),
      ...p.members.map((m) => m.clientName),
    ].join(' ').toLowerCase();
    return {
      ...p,
      label,
      vendor,
      clientCount: clients.length,
      clients,
      haystack,
      tokens: p.members[0] ? p.members[0].tokens : [],
    };
  }).sort((a, b) => b.clientCount - a.clientCount || b.members.length - a.members.length
    || a.label.localeCompare(b.label));

  const unlinked = clientApps.filter((a) => !a.catalogId);

  return {
    clientApps,
    catalogEntries,
    catalogById,
    tenantsById,
    idf,
    df,
    tokenIndex,
    catalogIndex,
    products: productList,
    unlinked,
  };
}

// Candidates for one unlinked app: catalog entries first, then same-product apps
// at OTHER clients. Same-client neighbours are not proposed — two rows in one
// client's estate are that client's own duplication problem, not a cross-client
// product match.
function candidatesFor(app, model) {
  const { idf, df, tokenIndex, catalogIndex, clientApps, catalogEntries } = model;
  const seedTokens = [...new Set(app.tokens)].filter((t) => (df.get(t) || 0) <= DF_CAP);
  const out = [];

  const catIds = new Set();
  seedTokens.forEach((t) => (catalogIndex.get(t) || []).forEach((i) => catIds.add(i)));
  catIds.forEach((i) => {
    const c = catalogEntries[i];
    const m = scorePair(app, c, idf);
    if (m && m.score >= MIN_SCORE) out.push({ kind: 'catalog', target: c, ...m });
  });

  const appIdxs = new Set();
  seedTokens.forEach((t) => (tokenIndex.get(t) || []).forEach((i) => appIdxs.add(i)));
  appIdxs.forEach((i) => {
    const peer = clientApps[i];
    if (peer.id === app.id || peer.tenantId === app.tenantId) return;
    const m = scorePair(app, peer, idf);
    if (m && m.score >= MIN_SCORE) out.push({ kind: 'peer', target: peer, ...m });
  });

  return out
    .sort((a, b) => b.score - a.score
      || (a.kind === 'catalog' ? -1 : 1) - (b.kind === 'catalog' ? -1 : 1)
      || String(a.target.name).localeCompare(String(b.target.name)))
    .slice(0, MAX_CANDIDATES);
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------
function MasterOnlyBanner() {
  return (
    <Alert
      severity="warning"
      icon={<LockIcon fontSize="inherit" />}
      sx={{ mb: 2, borderLeft: `4px solid ${NAVY}` }}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>Master only — contains named client data</AlertTitle>
      Every table on this screen names the client and shows their own application names, versions, owners and
      notes. It is Optimum-internal. Do not screen-share it on a client call, and do not send its exports outside
      Optimum. The anonymised, client-safe version of cross-client knowledge is Benchmarks.
    </Alert>
  );
}

function SectionHeading({ icon, title, subtitle }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1.5 }}>
      {icon}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>{title}</Typography>
        {subtitle && <Typography variant="body2" sx={{ color: GREY }}>{subtitle}</Typography>}
      </Box>
    </Stack>
  );
}

function ClientChip({ name, kind }) {
  const sample = kind === 'sample';
  return (
    <Chip
      size="small"
      label={sample ? `${name} · demo` : name}
      sx={{
        bgcolor: sample ? '#F3E5F5' : '#eef3ff',
        color: sample ? '#6A1B9A' : BLUE,
        fontWeight: 600,
        maxWidth: 260,
      }}
    />
  );
}

// A score is never shown without its band and its evidence. That is the whole
// contract of this screen: propose, evidence, human confirms.
function ScoreChip({ match }) {
  const band = bandOf(match);
  return (
    <Tooltip
      title={
        match.exact
          ? 'Identical after normalisation — same product name, different decoration.'
          : `Distinctiveness-weighted containment. Matched: ${match.matched.join(', ') || '—'}. `
            + `Only in this app: ${match.unmatchedLeft.join(', ') || '—'}. `
            + `Only in the candidate: ${match.unmatchedRight.join(', ') || '—'}.`
      }
    >
      <Chip
        size="small"
        label={`${band.label} · ${pct(match.score)}`}
        sx={{ bgcolor: band.bg, color: band.color, fontWeight: 700 }}
      />
    </Tooltip>
  );
}

function Evidence({ match }) {
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
        <Typography variant="caption" sx={{ color: GREY }}>matched</Typography>
        {match.matched.map((t) => (
          <Chip key={t} size="small" label={t} sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 700, height: 20 }} />
        ))}
      </Stack>
      {(match.unmatchedLeft.length > 0 || match.unmatchedRight.length > 0) && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
          <Typography variant="caption" sx={{ color: GREY }}>unmatched</Typography>
          {[...match.unmatchedLeft, ...match.unmatchedRight].slice(0, 8).map((t, i) => (
            <Chip key={`${t}-${i}`} size="small" label={t} sx={{ bgcolor: '#f1f3f6', color: GREY, height: 20 }} />
          ))}
        </Stack>
      )}
      {match.flags.length > 0 && (
        <Typography variant="caption" sx={{ color: '#B26A00', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 14 }} />
          {match.flags.join(' · ')}
        </Typography>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// TAB 1 — Product lookup. The on-a-call view.
// ---------------------------------------------------------------------------
function LookupTab({ model }) {
  const [q, setQ] = useState('');
  const [minClients, setMinClients] = useState(1);

  // The index is built once in the model; searching only filters it, so typing
  // stays in the sub-frame range even at 584 apps.
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const terms = needle ? needle.split(/\s+/) : [];
    return model.products.filter((p) => {
      if (p.clientCount < minClients) return false;
      if (!terms.length) return true;
      return terms.every((t) => p.haystack.includes(t));
    });
  }, [model.products, q, minClients]);

  const shown = useMemo(() => matches.slice(0, GROUP_CAP), [matches]);

  // For a product only one client runs, the same product may already be sitting
  // at another client under a different name. Surface those as UNCONFIRMED
  // possibilities — never as fact.
  const hints = useMemo(() => {
    const map = new Map();
    shown.forEach((p) => {
      if (p.clientCount > 1 || !p.members.length) return;
      const seen = new Set(p.members.map((m) => m.tenantId));
      const found = [];
      p.members.forEach((m) => {
        candidatesFor(m, model).forEach((c) => {
          if (c.kind !== 'peer' || seen.has(c.target.tenantId)) return;
          if (found.some((x) => x.target.id === c.target.id)) return;
          found.push(c);
        });
      });
      if (found.length) map.set(p.id, found.slice(0, 3));
    });
    return map;
  }, [shown, model]);

  const onExport = () => {
    const rows = [];
    matches.forEach((p) => {
      p.members.forEach((m) => {
        rows.push({ product: p.label, linked: p.linked ? 'linked' : 'unlinked (grouped by name)', clientCount: p.clientCount, app: m });
      });
    });
    exportCsv('primordia-product-lookup.csv', rows, [
      { label: 'Product', value: 'product' },
      { label: 'Grouping', value: 'linked' },
      { label: 'Clients running it', value: 'clientCount' },
      { label: 'Client', value: (r) => r.app.clientName },
      { label: 'Their app name', value: (r) => r.app.name },
      ...COMPARE_ROWS.filter((c) => c.key !== 'name').map((c) => ({ label: c.label, value: (r) => c.of(r.app) })),
      ...NOTE_ROWS.map((c) => ({ label: c.label, value: (r) => c.of(r.app) })),
    ]);
  };

  const multi = model.products.filter((p) => p.clientCount > 1).length;

  return (
    <Box>
      <SectionHeading
        icon={<SearchIcon sx={{ color: 'sectionHeader.main' }} />}
        title="Product lookup — who else runs this, and what happened"
        subtitle={'Type what the prospect just said. Results group by product: a catalog entry where the app has been '
          + 'linked, otherwise by normalised name. Clients sit in columns so the differences read at a glance — and the '
          + 'differences are the consulting insight.'}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          autoFocus
          placeholder="Omnicell, Alaris, Zoom…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 320 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
        <TextField
          select
          size="small"
          label="Clients running it"
          value={minClients}
          onChange={(e) => setMinClients(Number(e.target.value))}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value={1}>Any</MenuItem>
          <MenuItem value={2}>2 or more</MenuItem>
          <MenuItem value={3}>3 or more</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={onExport} disabled={!matches.length}>
          Export results (CSV)
        </Button>
      </Stack>

      <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
        {plural(matches.length, 'product')} match{matches.length === 1 ? 'es' : ''} of{' '}
        {plural(model.products.length, 'product group')} across {plural(model.clientApps.length, 'client application')}.{' '}
        {plural(multi, 'product')} {multi === 1 ? 'is' : 'are'} currently seen at more than one client.
        {matches.length > shown.length && ` Showing the first ${GROUP_CAP}; narrow the search to see the rest.`}
      </Typography>

      {!matches.length && (
        <Alert severity="info">
          Nothing matches &ldquo;{q}&rdquo;. That means no client&rsquo;s application name contains it — it does not mean
          no client runs it. Names differ wildly between clients; try the vendor, or a single distinctive word.
        </Alert>
      )}

      {shown.map((p) => (
        <ProductCard key={p.id} product={p} hints={hints.get(p.id) || []} />
      ))}
    </Box>
  );
}

function ProductCard({ product, hints }) {
  const members = product.members.slice(0, 8);
  const overflow = product.members.length - members.length;

  const valuesFor = (row) => members.map((m) => row.of(m));
  const differs = (row) => {
    if (!row.diff) return false;
    const vals = new Set(valuesFor(row).filter(Boolean));
    return vals.size > 1;
  };

  // The sentence a consultant says out loud.
  const summary = (() => {
    if (product.clientCount === 1) return `Only ${product.members[0].clientName} runs this today.`;
    const bits = members.map((m) => {
      const v = f(m, 'version');
      const h = f(m, 'hostingType');
      const tail = [v, h].filter(Boolean).join(', ');
      return tail ? `${m.clientName} (${tail})` : m.clientName;
    });
    const diffLabels = [...COMPARE_ROWS].filter(differs).map((r) => r.label.toLowerCase());
    const tail = diffLabels.length ? ` — ${diffLabels.slice(0, 4).join(', ')} differ between them.` : '';
    return `${plural(product.clientCount, 'client')} run this: ${bits.join('; ')}.${tail}`;
  })();

  return (
    <Paper sx={{ mb: 2, p: 2, borderLeft: `4px solid ${product.clientCount > 1 ? BLUE : '#d5dae1'}` }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NAVY }}>{product.label}</Typography>
        {product.vendor && <Typography variant="body2" sx={{ color: GREY }}>{product.vendor}</Typography>}
        <Chip
          size="small"
          label={`${plural(product.clientCount, 'client')}`}
          sx={{ bgcolor: product.clientCount > 1 ? '#E7F1FA' : '#f1f3f6', color: product.clientCount > 1 ? BLUE : GREY, fontWeight: 700 }}
        />
        {product.linked ? (
          <Chip size="small" icon={<LinkIcon sx={{ fontSize: 14 }} />} label="linked to catalog" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }} />
        ) : (
          <Tooltip title="Grouped by normalised name only. Two clients who name this product differently will appear as separate groups until someone links them in the Link to catalog tab.">
            <Chip size="small" label="grouped by name — not linked" sx={{ bgcolor: '#f1f3f6', color: GREY, fontWeight: 600 }} />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="body2" sx={{ color: GREY, mb: 1.5 }}>{summary}</Typography>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main', verticalAlign: 'bottom' } }}>
              <TableCell sx={{ width: 160 }}>Attribute</TableCell>
              {members.map((m) => (
                <TableCell key={m.id}>
                  <Stack spacing={0.5}>
                    <ClientChip name={m.clientName} kind={m.clientKind} />
                    <Typography variant="caption" sx={{ color: GREY }}>{m.name}</Typography>
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {COMPARE_ROWS.map((row) => {
              const isDiff = differs(row);
              return (
                <TableRow key={row.key} hover>
                  <TableCell sx={{ color: GREY }}>
                    {row.label}
                    {isDiff && (
                      <Chip
                        size="small"
                        label="differs"
                        sx={{ ml: 0.5, height: 18, bgcolor: DIFF_BG, color: '#B26A00', fontWeight: 700, fontSize: 10 }}
                      />
                    )}
                  </TableCell>
                  {members.map((m) => {
                    const v = row.of(m);
                    return (
                      <TableCell
                        key={m.id}
                        sx={{
                          bgcolor: isDiff ? DIFF_BG : 'transparent',
                          borderLeft: isDiff ? `2px solid ${DIFF_LINE}` : undefined,
                          fontWeight: isDiff ? 600 : 400,
                          color: v ? NAVY : '#aaa',
                        }}
                      >
                        {v || '—'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            {NOTE_ROWS.map((row) => {
              const vals = members.map((m) => row.of(m));
              if (!vals.some(Boolean)) return null;
              return (
                <TableRow key={row.key} hover>
                  <TableCell sx={{ color: GREY }}>{row.label}</TableCell>
                  {members.map((m, i) => (
                    <TableCell key={m.id} sx={{ minWidth: 220, color: vals[i] ? NAVY : '#aaa' }}>
                      <Typography variant="body2">{vals[i] || '—'}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {overflow > 0 && (
        <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
          {plural(overflow, 'further instance')} in this group {overflow === 1 ? 'is' : 'are'} not shown — the comparison
          is capped at 8 columns so it stays readable on a call.
        </Typography>
      )}

      {hints.length > 0 && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#FBFCFE', border: '1px dashed #c8d3e0', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: '#B26A00', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningAmberIcon sx={{ fontSize: 15 }} />
            Possibly the same product elsewhere — UNCONFIRMED, do not say this out loud as fact
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {hints.map((h) => (
              <Stack key={h.target.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <ScoreChip match={h} />
                <ClientChip name={h.target.clientName} kind={h.target.clientKind} />
                <Typography variant="body2" sx={{ color: NAVY }}>{h.target.name}</Typography>
                <Typography variant="caption" sx={{ color: GREY }}>
                  matched on {h.matched.join(', ')}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.75 }}>
            Confirm or reject these in the &ldquo;Link to catalog&rdquo; tab. Roughly a third of name-only matches at this
            strength turn out to be different products.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// TAB 2 — Link to catalog. The workflow that makes Tab 1 work.
// ---------------------------------------------------------------------------
function LinkTab({ model, onLink, onCreateEntry, busy }) {
  const [q, setQ] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [only, setOnly] = useState('all'); // all | candidates | none
  const [confirmLink, setConfirmLink] = useState(null);
  const [createFor, setCreateFor] = useState(null);
  const [createName, setCreateName] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  // Candidates for every unlinked app, computed once per model.
  const rows = useMemo(
    () => model.unlinked.map((a) => ({ app: a, candidates: candidatesFor(a, model) })),
    [model]
  );

  // Exact groups: unlinked apps at 2+ DIFFERENT clients whose normalised names
  // are identical. This is the ONLY thing eligible for bulk confirm — there is
  // no judgement call in "these two strings are the same string".
  const exactGroups = useMemo(() => {
    const byKey = new Map();
    model.unlinked.forEach((a) => {
      if (!a.key) return;
      if (!byKey.has(a.key)) byKey.set(a.key, []);
      byKey.get(a.key).push(a);
    });
    return [...byKey.entries()]
      .map(([key, members]) => ({
        key,
        members,
        clients: [...new Set(members.map((m) => m.tenantId))],
        name: canonicalName(members.slice().sort((a, b) => a.name.length - b.name.length)[0].name),
      }))
      .filter((g) => g.clients.length > 1)
      .sort((a, b) => b.clients.length - a.clients.length || a.name.localeCompare(b.name));
  }, [model]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (clientFilter !== 'all' && r.app.tenantId !== clientFilter) return false;
      if (only === 'candidates' && !r.candidates.length) return false;
      if (only === 'none' && r.candidates.length) return false;
      if (!needle) return true;
      return `${r.app.name} ${r.app.clientName} ${f(r.app, 'vendorName')}`.toLowerCase().includes(needle);
    }).sort((a, b) => {
      const sa = a.candidates.length ? a.candidates[0].score : -1;
      const sb = b.candidates.length ? b.candidates[0].score : -1;
      return sb - sa || a.app.name.localeCompare(b.app.name);
    });
  }, [rows, q, clientFilter, only]);

  const shown = filtered.slice(0, ROW_CAP);
  const clients = useMemo(
    () => [...new Map(model.clientApps.map((a) => [a.tenantId, a.clientName])).entries()]
      .sort((a, b) => a[1].localeCompare(b[1])),
    [model.clientApps]
  );

  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const onExport = () => {
    exportCsv('primordia-link-candidates.csv', filtered, [
      { label: 'Client', value: (r) => r.app.clientName },
      { label: 'Their app name', value: (r) => r.app.name },
      { label: 'Normalised', value: (r) => r.app.key },
      { label: 'Vendor', value: (r) => f(r.app, 'vendorName') },
      { label: 'Candidates', value: (r) => r.candidates.length },
      { label: 'Best candidate', value: (r) => (r.candidates.length ? r.candidates[0].target.name : '') },
      { label: 'Best candidate is', value: (r) => (r.candidates.length ? (r.candidates[0].kind === 'catalog' ? 'catalog entry' : `app at ${r.candidates[0].target.clientName}`) : '') },
      { label: 'Score', value: (r) => (r.candidates.length ? pct(r.candidates[0].score) : '') },
      { label: 'Band', value: (r) => (r.candidates.length ? bandOf(r.candidates[0]).label : '') },
      { label: 'Matched tokens', value: (r) => (r.candidates.length ? r.candidates[0].matched.join(' ') : '') },
      { label: 'Caveats', value: (r) => (r.candidates.length ? r.candidates[0].flags.join('; ') : '') },
      { label: 'Status', value: () => 'PROPOSED — not linked' },
    ]);
  };

  const withCandidates = rows.filter((r) => r.candidates.length).length;

  return (
    <Box>
      <SectionHeading
        icon={<LinkIcon sx={{ color: 'sectionHeader.main' }} />}
        title="Link to catalog — propose, evidence, confirm"
        subtitle={'Nothing here links itself. Each row proposes candidates with a score and the tokens behind it; you '
          + 'confirm one, or none. A wrong link tells a consultant on a live call that a client runs software they do not.'}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle sx={{ fontWeight: 700 }}>How the score is built, so you can argue with it</AlertTitle>
        Names are lower-cased, punctuation stripped, then client initialisms (dfci, nghs, musc, muha, humi, enta) and
        corporate filler (inc, llc, systems, solutions, technologies, medical, health) are removed. Overlap is scored by
        <b> containment</b>, not Jaccard — &ldquo;BD Alaris&rdquo; and &ldquo;Alaris Systems Manager&rdquo; share one word
        of four, which Jaccard would bury. Each shared word is weighted by how rare it is across the estate: sharing
        &ldquo;alaris&rdquo; is evidence, sharing &ldquo;manager&rdquo; is not. A match whose shorter name is a single word
        is capped at {pct(SINGLE_TOKEN_CEILING)} — &ldquo;InSight&rdquo; sits inside &ldquo;vRealize Log Insight&rdquo; and they are
        unrelated. Only an identical normalised name ever reads 100%.
      </Alert>

      {exactGroups.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, borderLeft: `4px solid #2E7D32` }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>
            Exact normalised matches across clients — safe to bulk confirm
          </Typography>
          <Typography variant="body2" sx={{ color: GREY, mb: 1.5 }}>
            {plural(exactGroups.length, 'group')} of applications at two or more different clients whose names are
            identical once normalised. There is no judgement in these, so they may be confirmed together. Confirming a
            group creates one catalog entry and links every member to it. Fuzzy candidates are never eligible for this.
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected.size > 0 && selected.size === exactGroups.length}
                      indeterminate={selected.size > 0 && selected.size < exactGroups.length}
                      onChange={() => setSelected(
                        selected.size === exactGroups.length ? new Set() : new Set(exactGroups.map((g) => g.key))
                      )}
                    />
                  </TableCell>
                  <TableCell>Proposed catalog entry</TableCell>
                  <TableCell>Normalised name</TableCell>
                  <TableCell>Clients</TableCell>
                  <TableCell align="right">Applications</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exactGroups.slice(0, ROW_CAP).map((g) => (
                  <TableRow key={g.key} hover>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selected.has(g.key)} onChange={() => toggle(g.key)} />
                    </TableCell>
                    <TableCell><b style={{ color: NAVY }}>{g.name}</b></TableCell>
                    <TableCell sx={{ color: GREY, fontFamily: 'monospace', fontSize: 12 }}>{g.key}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {g.members.map((m) => (
                          <ClientChip key={m.id} name={m.clientName} kind={m.clientKind} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{g.members.length}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
            <Button
              variant="contained"
              size="small"
              disabled={!selected.size || busy}
              onClick={() => setBulkOpen(true)}
              sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#0e1d36' } }}
            >
              Confirm {selected.size} selected group{selected.size === 1 ? '' : 's'}
            </Button>
            <Typography variant="caption" sx={{ color: GREY }}>
              Exact matches only. Everything below requires a per-link confirmation.
            </Typography>
          </Stack>
        </Paper>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <TextField
          size="small"
          placeholder="Filter unlinked applications…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <TextField select size="small" label="Client" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} sx={{ minWidth: 220 }}>
          <MenuItem value="all">All clients</MenuItem>
          {clients.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Show" value={only} onChange={(e) => setOnly(e.target.value)} sx={{ minWidth: 220 }}>
          <MenuItem value="all">All unlinked</MenuItem>
          <MenuItem value="candidates">With candidates</MenuItem>
          <MenuItem value="none">No candidate found</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={onExport} disabled={!filtered.length}>
          Export candidates (CSV)
        </Button>
      </Stack>

      <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
        {plural(model.unlinked.length, 'client application')} unlinked of {model.clientApps.length}.{' '}
        {plural(withCandidates, 'of them has')} at least one candidate above {pct(MIN_SCORE)}.{' '}
        {plural(filtered.length, 'row')} match the filter{filtered.length > shown.length ? `; showing the first ${ROW_CAP}` : ''}.
      </Typography>

      {!filtered.length && (
        <Alert severity="success">Nothing unlinked matches this filter.</Alert>
      )}

      {shown.map(({ app, candidates }) => (
        <Paper key={app.id} sx={{ p: 2, mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <ClientChip name={app.clientName} kind={app.clientKind} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY }}>{app.name}</Typography>
            {f(app, 'vendorName') && <Typography variant="caption" sx={{ color: GREY }}>{f(app, 'vendorName')}</Typography>}
            {f(app, 'version') && <Chip size="small" label={`v${f(app, 'version')}`} sx={{ bgcolor: '#fff3e0', color: '#8a5a00', height: 20 }} />}
            <Typography variant="caption" sx={{ color: GREY, fontFamily: 'monospace' }}>→ {app.key || '(no usable tokens)'}</Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              disabled={busy}
              onClick={() => { setCreateFor(app); setCreateName(canonicalName(app.name)); }}
            >
              Create new catalog entry
            </Button>
          </Stack>

          {app.danglingCatalogId && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              This application carries catalogId <b>{app.danglingCatalogId}</b>, which resolves to no catalog entry. It is
              treated as unlinked rather than trusted.
            </Alert>
          )}

          {!candidates.length ? (
            <Typography variant="body2" sx={{ color: GREY }}>
              No candidate above {pct(MIN_SCORE)}. Either this product is genuinely new to the corpus, or the name shares
              no distinctive word with anything we hold. Create a catalog entry from it.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                  <TableCell sx={{ width: 130 }}>Score</TableCell>
                  <TableCell>Candidate</TableCell>
                  <TableCell>Evidence</TableCell>
                  <TableCell align="right" sx={{ width: 150 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {candidates.map((c) => (
                  <TableRow key={`${c.kind}-${c.target.id}`} hover>
                    <TableCell><ScoreChip match={c} /></TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>{c.target.name}</Typography>
                        {c.kind === 'catalog' ? (
                          <Typography variant="caption" sx={{ color: GREY }}>
                            catalog entry{c.target.vendor ? ` · ${c.target.vendor}` : ''}
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                            <Typography variant="caption" sx={{ color: GREY }}>app at</Typography>
                            <ClientChip name={c.target.clientName} kind={c.target.clientKind} />
                            {c.target.catalogId && (
                              <Chip size="small" label="already linked" sx={{ height: 18, bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }} />
                            )}
                          </Stack>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell><Evidence match={c} /></TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => setConfirmLink({ app, candidate: c })}
                      >
                        Confirm link
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      ))}

      {/* Per-link confirmation. Deliberately verbose: this is the moment the
          system stops guessing and starts asserting. */}
      <Dialog open={!!confirmLink} onClose={() => setConfirmLink(null)} maxWidth="sm" fullWidth>
        {confirmLink && (
          <>
            <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
              {confirmLink.candidate.exact ? 'Confirm this link' : 'Confirm this link — it is a proposal, not a fact'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                You are asserting that <b>{confirmLink.app.name}</b> at <b>{confirmLink.app.clientName}</b> is the same
                product as <b>{confirmLink.candidate.target.name}</b>
                {confirmLink.candidate.kind === 'peer' ? ` at ${confirmLink.candidate.target.clientName}` : ' in the catalog'}.
                Once linked, this appears in Product lookup as established fact and gets quoted on calls.
              </DialogContentText>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ScoreChip match={confirmLink.candidate} />
                  <Typography variant="body2" sx={{ color: GREY }}>
                    {confirmLink.candidate.exact
                      ? 'Identical after normalisation.'
                      : 'Name similarity only. The system has not seen a vendor confirmation, a licence key or a version match.'}
                  </Typography>
                </Stack>
                <Evidence match={confirmLink.candidate} />
                {confirmLink.candidate.kind === 'peer' && !confirmLink.candidate.target.catalogId && (
                  <Alert severity="info">
                    Neither application is linked yet, so confirming creates one catalog entry
                    (&ldquo;{canonicalName(confirmLink.candidate.target.name)}&rdquo;) and links both to it.
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmLink(null)}>Cancel</Button>
              <Button
                variant="contained"
                disabled={busy}
                sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#0e1d36' } }}
                onClick={() => {
                  const payload = confirmLink;
                  setConfirmLink(null);
                  onLink(payload.app, payload.candidate);
                }}
              >
                Yes — link them
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create a catalog entry from an application that matched nothing. */}
      <Dialog open={!!createFor} onClose={() => setCreateFor(null)} maxWidth="sm" fullWidth>
        {createFor && (
          <>
            <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Create a catalog entry from this application</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                This creates a canonical entry in the Optimum master workspace and links{' '}
                <b>{createFor.name}</b> ({createFor.clientName}) to it. Give it the name you would want to see on a call —
                the client&rsquo;s own decoration has already been stripped.
              </DialogContentText>
              <TextField
                fullWidth
                size="small"
                label="Catalog entry name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreateFor(null)}>Cancel</Button>
              <Button
                variant="contained"
                disabled={busy || !createName.trim()}
                sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#0e1d36' } }}
                onClick={() => {
                  const app = createFor;
                  const name = createName.trim();
                  setCreateFor(null);
                  onCreateEntry(name, [app]);
                }}
              >
                Create and link
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Bulk confirm — exact normalised matches only. */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Confirm exact matches</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {plural(selected.size, 'group')} will each get one catalog entry, and every member application will be linked
            to it. Only groups whose names are identical after normalisation are eligible — no fuzzy candidate is
            included here.
          </DialogContentText>
          <Stack spacing={0.75}>
            {exactGroups.filter((g) => selected.has(g.key)).map((g) => (
              <Typography key={g.key} variant="body2" sx={{ color: NAVY }}>
                <b>{g.name}</b> — {plural(g.members.length, 'application')} across {plural(g.clients.length, 'client')}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy}
            sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#0e1d36' } }}
            onClick={() => {
              const groups = exactGroups.filter((g) => selected.has(g.key));
              setBulkOpen(false);
              setSelected(new Set());
              groups.forEach((g) => onCreateEntry(g.name, g.members));
            }}
          >
            Confirm {selected.size} group{selected.size === 1 ? '' : 's'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TAB 3 — Coverage. Lead with what is NOT linked, because that is the work.
// ---------------------------------------------------------------------------
function CoverageTab({ model }) {
  const perClient = useMemo(() => {
    const map = new Map();
    model.clientApps.forEach((a) => {
      if (!map.has(a.tenantId)) {
        map.set(a.tenantId, { id: a.tenantId, name: a.clientName, kind: a.clientKind, total: 0, linked: 0 });
      }
      const row = map.get(a.tenantId);
      row.total += 1;
      if (a.catalogId) row.linked += 1;
    });
    return [...map.values()]
      .map((r) => ({ ...r, unlinked: r.total - r.linked, pct: r.total ? Math.round((r.linked / r.total) * 100) : 0 }))
      .sort((a, b) => b.unlinked - a.unlinked || a.name.localeCompare(b.name));
  }, [model.clientApps]);

  const entries = useMemo(() => {
    const byEntry = new Map(model.catalogEntries.map((c) => [c.id, { entry: c, clients: new Set(), apps: 0 }]));
    model.clientApps.forEach((a) => {
      if (!a.catalogId || !byEntry.has(a.catalogId)) return;
      const row = byEntry.get(a.catalogId);
      row.clients.add(a.tenantId);
      row.apps += 1;
    });
    return [...byEntry.values()]
      .map((r) => ({ ...r, clientCount: r.clients.size, clientNames: [...r.clients].map((id) => (model.tenantsById.get(id) || {}).name || id) }))
      .sort((a, b) => b.clientCount - a.clientCount || b.apps - a.apps || a.entry.name.localeCompare(b.entry.name));
  }, [model.catalogEntries, model.clientApps, model.tenantsById]);

  const multi = entries.filter((e) => e.clientCount > 1);
  const single = entries.filter((e) => e.clientCount === 1);
  const orphan = entries.filter((e) => e.clientCount === 0);

  const totalLinked = model.clientApps.length - model.unlinked.length;
  const overallPct = model.clientApps.length ? Math.round((totalLinked / model.clientApps.length) * 100) : 0;

  const onExport = () => {
    exportCsv('primordia-catalog-coverage.csv', perClient, [
      { label: 'Client', value: 'name' },
      { label: 'Workspace kind', value: 'kind' },
      { label: 'Applications', value: 'total' },
      { label: 'Linked', value: 'linked' },
      { label: 'Unlinked', value: 'unlinked' },
      { label: 'Linked %', value: 'pct' },
    ]);
  };

  const onExportEntries = () => {
    exportCsv('primordia-catalog-entries.csv', entries, [
      { label: 'Catalog entry', value: (r) => r.entry.name },
      { label: 'Vendor', value: (r) => r.entry.vendor },
      { label: 'Source', value: (r) => (r.entry.source === 'seed' ? 'base catalog' : 'created from a client app') },
      { label: 'Clients', value: 'clientCount' },
      { label: 'Applications linked', value: 'apps' },
      { label: 'Client names', value: (r) => r.clientNames.join('; ') },
    ]);
  };

  return (
    <Box>
      <SectionHeading
        icon={<DonutLargeIcon sx={{ color: 'sectionHeader.main' }} />}
        title="Coverage — how much of the estate is actually linked"
        subtitle={'The unlinked count is the number that matters: an application nobody has linked is invisible to '
          + 'Product lookup, no matter how much detail it carries.'}
      />

      <Paper sx={{ p: 2, mb: 2, borderLeft: `4px solid ${overallPct > 0 ? BLUE : '#C62828'}` }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: overallPct > 0 ? NAVY : '#C62828' }}>
          {model.unlinked.length} unlinked
        </Typography>
        <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
          of {plural(model.clientApps.length, 'client application')} across {plural(perClient.length, 'client workspace')}
          {' '}— {overallPct}% linked to a catalog entry. {plural(model.catalogEntries.length, 'catalog entry')} exist{model.catalogEntries.length === 1 ? 's' : ''}.
        </Typography>
        <LinearProgress
          variant="determinate"
          value={overallPct}
          sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: overallPct >= 50 ? '#2E7D32' : '#F5A623' } }}
        />
      </Paper>

      <Stack direction="row" sx={{ mb: 1 }} alignItems="center">
        <Typography variant="overline" sx={{ color: GREY }}>Per client</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={onExport}>Export coverage (CSV)</Button>
      </Stack>
      <Paper sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
              <TableCell>Client</TableCell>
              <TableCell align="right">Unlinked</TableCell>
              <TableCell align="right">Linked</TableCell>
              <TableCell align="right">Applications</TableCell>
              <TableCell sx={{ width: 180 }}>Linked %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {perClient.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell><ClientChip name={r.name} kind={r.kind} /></TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: r.unlinked ? '#C62828' : GREY }}>{r.unlinked}</TableCell>
                <TableCell align="right">{r.linked}</TableCell>
                <TableCell align="right">{r.total}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LinearProgress
                      variant="determinate"
                      value={r.pct}
                      sx={{ flex: 1, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: r.pct >= 50 ? '#2E7D32' : '#F5A623' } }}
                    />
                    <Typography variant="caption" sx={{ color: GREY, minWidth: 32 }}>{r.pct}%</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Stack direction="row" sx={{ mb: 1 }} alignItems="center">
        <Typography variant="overline" sx={{ color: GREY }}>
          Catalog entries at two or more clients — the comparable ones
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={onExportEntries}>Export entries (CSV)</Button>
      </Stack>
      {!multi.length ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>No catalog entry is yet shared by two clients</AlertTitle>
          Until an entry has two clients behind it there is nothing to compare and contrast, which is the entire point of
          this screen. The fastest route is the exact-match groups in the Link to catalog tab.
        </Alert>
      ) : (
        <Paper sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                <TableCell>Catalog entry</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell align="right">Clients</TableCell>
                <TableCell>Which</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {multi.slice(0, ROW_CAP).map((r) => (
                <TableRow key={r.entry.id} hover>
                  <TableCell><b style={{ color: NAVY }}>{r.entry.name}</b></TableCell>
                  <TableCell sx={{ color: GREY }}>{r.entry.vendor || '—'}</TableCell>
                  <TableCell align="right"><b>{r.clientCount}</b></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {r.clientNames.map((n) => <Chip key={n} size="small" label={n} sx={{ bgcolor: '#eef3ff', color: BLUE, fontWeight: 600 }} />)}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Typography variant="overline" sx={{ color: GREY }}>Catalog entries at exactly one client</Typography>
      <Paper sx={{ mb: 3, mt: 0.5, p: single.length ? 0 : 2 }}>
        {!single.length ? (
          <Typography variant="body2" sx={{ color: GREY }}>None.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                <TableCell>Catalog entry</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Client</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {single.slice(0, ROW_CAP).map((r) => (
                <TableRow key={r.entry.id} hover>
                  <TableCell>{r.entry.name}</TableCell>
                  <TableCell sx={{ color: GREY }}>{r.entry.vendor || '—'}</TableCell>
                  <TableCell><Chip size="small" label={r.clientNames[0]} sx={{ bgcolor: '#eef3ff', color: BLUE, fontWeight: 600 }} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Typography variant="overline" sx={{ color: GREY }}>Catalog entries with no client at all</Typography>
      <Paper sx={{ mt: 0.5, p: orphan.length ? 0 : 2 }}>
        {!orphan.length ? (
          <Typography variant="body2" sx={{ color: GREY }}>None — every catalog entry has at least one client behind it.</Typography>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                  <TableCell>Catalog entry</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orphan.slice(0, ROW_CAP).map((r) => (
                  <TableRow key={r.entry.id} hover>
                    <TableCell>{r.entry.name}</TableCell>
                    <TableCell sx={{ color: GREY }}>{r.entry.vendor || '—'}</TableCell>
                    <TableCell sx={{ color: GREY }}>{r.entry.source === 'seed' ? 'base catalog' : 'created from a client app'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Divider />
            <Typography variant="caption" sx={{ color: GREY, display: 'block', p: 1.5 }}>
              An entry with no client is a definition nobody has claimed yet. That is not necessarily wrong — but it earns
              nothing until an application is linked to it.
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------
export default function CatalogIntelligence({ tenantId }) {
  // MASTER ONLY. Same gate as Benchmarks: in deployed environments the Cognito
  // group is the authority; with auth switched off (local dev) the master
  // workspace stands in for it. Unlike Benchmarks, nothing on this screen is
  // anonymised, so this gate is the ONLY thing standing between one client and
  // another client's named estate. Do not weaken it.
  const allowed = isMaster() || (!getConfig().authRequired && tenantId === MASTER_TENANT);

  const [apps, setApps] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    let live = true;
    if (!allowed) {
      // Not merely hidden — not fetched. A refused user never causes a read of
      // another client's rows.
      setLoaded(true);
      return () => { live = false; };
    }
    Promise.all([
      store.listFactSheets(MASTER_TENANT, { type: 'Application' }),
      store.listCatalog(),
      store.listTenants(),
    ]).then(([rows, cat, tens]) => {
      if (!live) return;
      setApps(rows);
      setCatalog(cat || []);
      setTenants(tens || []);
      setLoaded(true);
    });
    return () => { live = false; };
  }, [allowed]);

  const model = useMemo(() => buildModel(apps, catalog, tenants), [apps, catalog, tenants]);

  const upsert = useCallback((saved) => {
    setApps((prev) => {
      const i = prev.findIndex((x) => x.id === saved.id);
      if (i < 0) return [...prev, saved];
      const next = prev.slice();
      next[i] = saved;
      return next;
    });
  }, []);

  const linkApp = useCallback(async (app, catalogId) => {
    const saved = await store.saveFactSheet({
      ...app,
      fields: { ...(app.fields || {}), catalogId },
    });
    if (saved) upsert(saved);
    return saved;
  }, [upsert]);

  // Create a canonical entry in the master workspace and link the given apps to
  // it. The entry is an Application fact sheet in the master tenant whose
  // catalogId points at itself — a real, persistent record written through the
  // existing store API, and self-identifying as a catalog entry.
  const createEntry = useCallback(async (name, appsToLink) => {
    setBusy(true);
    try {
      const seedApp = appsToLink[0];
      const created = await store.createFactSheet(MASTER_TENANT, 'Application', name);
      if (!created) throw new Error('could not create the catalog entry');
      const entry = await store.saveFactSheet({
        ...created,
        name,
        fields: {
          ...(created.fields || {}),
          name,
          catalogId: created.id,
          description: f(seedApp, 'description'),
          vendorName: f(seedApp, 'vendorName'),
          hostingType: f(seedApp, 'hostingType'),
          timeClassification: f(seedApp, 'timeClassification'),
          sixRClassification: f(seedApp, 'sixRClassification'),
          lifecycleState: 'Active',
        },
        quality: 'Draft',
      });
      upsert(entry);
      for (const a of appsToLink) {
        // Sequential on purpose: the store writes through to the API per record.
        // eslint-disable-next-line no-await-in-loop
        await linkApp(a, entry.id);
      }
      setNote({
        severity: 'success',
        text: `Created catalog entry "${name}" and linked ${plural(appsToLink.length, 'application')}.`,
      });
    } catch (err) {
      setNote({ severity: 'error', text: `Could not create the catalog entry: ${err.message}` });
    } finally {
      setBusy(false);
    }
  }, [linkApp, upsert]);

  const confirmLink = useCallback(async (app, candidate) => {
    setBusy(true);
    try {
      if (candidate.kind === 'catalog') {
        await linkApp(app, candidate.target.id);
        setNote({ severity: 'success', text: `Linked "${app.name}" (${app.clientName}) to "${candidate.target.name}".` });
      } else if (candidate.target.catalogId) {
        await linkApp(app, candidate.target.catalogId);
        const entry = model.catalogById.get(candidate.target.catalogId);
        setNote({
          severity: 'success',
          text: `Linked "${app.name}" (${app.clientName}) to "${(entry && entry.name) || candidate.target.name}".`,
        });
      } else {
        setBusy(false);
        await createEntry(canonicalName(candidate.target.name), [candidate.target, app]);
        return;
      }
    } catch (err) {
      setNote({ severity: 'error', text: `Could not link: ${err.message}` });
    } finally {
      setBusy(false);
    }
  }, [linkApp, createEntry, model.catalogById]);

  if (!allowed) {
    // Plain refusal. No counts, no client names, no hint of what is behind it.
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" icon={<LockIcon />}>
          <AlertTitle>Catalog Intelligence is an Optimum-internal view</AlertTitle>
          This screen compares named client estates side by side and is available only to Optimum master administrators.
          Your workspace is never shown to another client, and no other client&rsquo;s data is shown to you. If you would
          like to know how comparable organisations have handled a particular application, ask your Optimum engagement
          lead — they can walk you through it.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }} flexWrap="wrap">
        <HubIcon sx={{ color: 'sectionHeader.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Catalog Intelligence</Typography>
        <Chip size="small" label="Optimum-internal · named client data" sx={{ bgcolor: '#FDECEA', color: '#C62828', fontWeight: 700 }} />
        {busy && <CircularProgress size={18} />}
      </Stack>
      <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
        The central list a consultant reaches for mid-call. When a prospect says &ldquo;we run X&rdquo;, this shows which
        existing clients run it, at which versions, hosted how, what disposition each chose and what went wrong — and it
        works because someone did the linking work in tab two. LeanIX cannot do this at all: it is single-tenant, so the
        comparison does not exist inside it.
      </Typography>

      <MasterOnlyBanner />

      {note && (
        <Alert severity={note.severity} onClose={() => setNote(null)} sx={{ mb: 2 }}>{note.text}</Alert>
      )}

      {!loaded ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" sx={{ color: GREY }}>Indexing every client application…</Typography>
        </Stack>
      ) : (
        <>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Tab label="Product lookup" sx={{ fontWeight: 700, textTransform: 'none' }} />
            <Tab label={`Link to catalog (${model.unlinked.length})`} sx={{ fontWeight: 700, textTransform: 'none' }} />
            <Tab label="Coverage" sx={{ fontWeight: 700, textTransform: 'none' }} />
          </Tabs>

          {tab === 0 && <LookupTab model={model} />}
          {tab === 1 && <LinkTab model={model} onLink={confirmLink} onCreateEntry={createEntry} busy={busy} />}
          {tab === 2 && <CoverageTab model={model} />}
        </>
      )}
    </Box>
  );
}
